import { t } from 'ttag';

const ADDED_TAG_STUDY_TYPE = {
  MCT: t`MCT`,
  'MCT PEEK': t`MCT PEEK`,
  'CARDIAC EVENT': t`CARDIAC EVENT`,
  HOLTER: t`HOLTER`,
  'EXTENDED HOLTER': t`EXTENDED HOLTER`,
};
/**
 * Resolves the translated label for a study type. Looks the raw value up in the ADDED_TAG_STUDY_TYPE message map and falls back to the value itself when no translation is registered.
 *
 * @param studyType - Raw study type value to translate.
 * @returns The translated label, or the original value when untranslated.
 */
const gettextStudyType = (studyType: any) => (ADDED_TAG_STUDY_TYPE as any)[studyType] || studyType;

const ADDED_TAG_PRIORITY = {
  'All priority': t`All priority`,
  Low: t`Low`,
  Medium: t`Medium`,
  High: t`High`,
  Urgent: t`Urgent`,
  Emergent: t`Emergent`,
};
/**
 * Resolves the translated label for a report priority. Looks the raw value up in the ADDED_TAG_PRIORITY message map and falls back to the value itself when no translation is registered.
 *
 * @param priority - Raw report priority value to translate.
 * @returns The translated label, or the original value when untranslated.
 */
const getttextPriority = (priority: any) => (ADDED_TAG_PRIORITY as any)[priority] || priority;

const ADDED_TAG_STUDY_STATUS = {
  Draft: t`Draft`,
  Starting: t`Starting`,
  Aborted: t`Aborted`,
  Started: t`Started`,
  Paused: t`Paused`,
  Resumed: t`Resumed`,
  Completed: t`Completed`,
  Uploading: t`Uploading`,
  Uploaded: t`Uploaded`,
  Erasing: t`Erasing`,
  Erased: t`Erased`,
  Stopping: t`Stopping`,
  Ongoing: t`Ongoing`,
  Done: t`Done`,
};
/**
 * Resolves the translated label for a study status. Looks the raw value up in the ADDED_TAG_STUDY_STATUS message map and falls back to the value itself when no translation is registered.
 *
 * @param studyStatus - Raw study status value to translate.
 * @returns The translated label, or the original value when untranslated.
 */
const gettextStudyStatus = (studyStatus: any) =>
  (ADDED_TAG_STUDY_STATUS as any)[studyStatus] || studyStatus;

const ADDED_TAG_REPORT_TYPE = {
  Interim: t`Interim`,
  Study: t`End Of Use`,
  Notification: t`Notification`,
  Event: t`Event`,
};
/**
 * Resolves the translated label for a report type. Looks the raw value up in the ADDED_TAG_REPORT_TYPE message map and falls back to the value itself when no translation is registered.
 *
 * @param reportType - Raw report type value to translate.
 * @returns The translated label, or the original value when untranslated.
 */
const gettextReportType = (reportType: any) =>
  (ADDED_TAG_REPORT_TYPE as any)[reportType] || reportType;

const ADDED_TAG_DEVICE_MODEL = {
  Bioflux: t`Bioflux`,
  Biotres: t`Biotres`,
};
/**
 * Resolves the translated label for a device model name. Looks the raw value up in the ADDED_TAG_DEVICE_MODEL message map and falls back to the value itself when no translation is registered.
 *
 * @param deviceModel - Raw device model name value to translate.
 * @returns The translated label, or the original value when untranslated.
 */
const gettextDeviceModel = (deviceModel: any) =>
  (ADDED_TAG_DEVICE_MODEL as any)[deviceModel] || deviceModel;

const ADDED_TAG_RECEIVED_ITEM = {
  Device: t`Device`,
  Accessories: t`Accessories`,
  Box: t`Box`,
  Cable: t`Cable`,
  Charger: t`Charger`,
  'Quick guide': t`Quick guide`,
};
/**
 * Resolves the translated label for a received RMA item. Looks the raw value up in the ADDED_TAG_RECEIVED_ITEM message map and falls back to the value itself when no translation is registered.
 *
 * @param receivedItem - Raw received RMA item value to translate.
 * @returns The translated label, or the original value when untranslated.
 */
const gettextReceivedItem = (receivedItem: any) =>
  (ADDED_TAG_RECEIVED_ITEM as any)[receivedItem] || receivedItem;

const ADDED_TAG_SUPPORT_REQUEST_ACTION = {
  'resolved this support request': t`resolved this support request`,
};
/**
 * Resolves the translated label for a support request action. Looks the raw value up in the ADDED_TAG_SUPPORT_REQUEST_ACTION message map and falls back to the value itself when no translation is registered.
 *
 * @param supportRequestAction - Raw support request action value to translate.
 * @returns The translated label, or the original value when untranslated.
 */
const gettextSupportRequestAction = (supportRequestAction: any) =>
  (ADDED_TAG_SUPPORT_REQUEST_ACTION as any)[supportRequestAction] || supportRequestAction;

const ADDED_TAG_FACILITY_NAME = {
  'All facilities': t`All facilities`,
};
/**
 * Resolves the translated label for a facility name. Looks the raw value up in the ADDED_TAG_FACILITY_NAME message map and falls back to the value itself when no translation is registered.
 *
 * @param facilityName - Raw facility name value to translate.
 * @returns The translated label, or the original value when untranslated.
 */
const gettextFacilityName = (facilityName: any) =>
  (ADDED_TAG_FACILITY_NAME as any)[facilityName] || facilityName;

const ADDED_TAG_SEE_TYPE = {
  'Your studies': t`Your studies`,
  'All studies': t`All studies`,
};
/**
 * Resolves the translated label for a see-all-patients selection type. Looks the value up in the ADDED_TAG_SEE_TYPE message map with no fallback, so unregistered types resolve to undefined.
 *
 * @param seeType - See-all-patients type key to translate.
 * @returns The translated label, or undefined when the key is unknown.
 */
const gettextSeeAllPatients = (seeType: any) => (ADDED_TAG_SEE_TYPE as any)[seeType];

const ADDED_TAG_GENDER = {
  Male: t`Male`,
  Female: t`Female`,
};
/**
 * Resolves the translated label for a patient gender. Looks the raw value up in the ADDED_TAG_GENDER message map and falls back to the value itself when no translation is registered.
 *
 * @param gender - Raw patient gender value to translate.
 * @returns The translated label, or the original value when untranslated.
 */
const gettextGender = (gender: any) => (ADDED_TAG_GENDER as any)[gender] || gender;

const ADDED_TAG_EDIT_PATIENT_DETAILS_TITLE = {
  'Patient information': t`Patient information`,
  'Patient contact': t`Patient contact`,
  'Secondary contact': t`Secondary contact`,
  Insurance: t`Insurance`,
  'Medical history': t`Medical history`,
};
/**
 * Resolves the translated label for a edit patient details section title. Looks the raw value up in the ADDED_TAG_EDIT_PATIENT_DETAILS_TITLE message map and falls back to the value itself when no translation is registered.
 *
 * @param editPatientDetailsTitle - Raw edit patient details section title value to translate.
 * @returns The translated label, or the original value when untranslated.
 */
const gettextEditPatientDetailsTitle = (editPatientDetailsTitle: any) =>
  (ADDED_TAG_EDIT_PATIENT_DETAILS_TITLE as any)[editPatientDetailsTitle] || editPatientDetailsTitle;

const ADDED_TAG_EVENT_TYPE = {
  'All event type': t`All event type`,
  Brady: t`Brady`,
  Tachy: t`Tachy`,
  AFib: t`AFib`,
  Pause: t`Pause`,
  Auto: t`Auto`,
  Sinus: t`Sinus`,
  Manual: t`Manual`,
};
/**
 * Resolves the translated label for a ECG event type. Looks the raw value up in the ADDED_TAG_EVENT_TYPE message map and falls back to the value itself when no translation is registered.
 *
 * @param eventType - Raw ECG event type value to translate.
 * @returns The translated label, or the original value when untranslated.
 */
const gettextEventType = (eventType: any) => (ADDED_TAG_EVENT_TYPE as any)[eventType] || eventType;

const ADDED_TAG_DEVICE_NOTIFICATION_HISTORY = {
  'Power On': t`Power On`,
  'Power Off': t`Power Off`,
  'Lead Disconnected': t`Lead Disconnected`,
};
/**
 * Resolves the translated label for a device notification history entry. Looks the raw value up in the ADDED_TAG_DEVICE_NOTIFICATION_HISTORY message map and falls back to the value itself when no translation is registered.
 *
 * @param history - Raw device notification history entry value to translate.
 * @returns The translated label, or the original value when untranslated.
 */
const gettextDeviceNotificationHistory = (history: any) =>
  (ADDED_TAG_DEVICE_NOTIFICATION_HISTORY as any)[history] || history;

const ADDED_TAG_PATIENT_IMPACT = {
  None: t`None`,
};
/**
 * Resolves the translated label for a patient impact level. Looks the raw value up in the ADDED_TAG_PATIENT_IMPACT message map and falls back to the value itself when no translation is registered.
 *
 * @param patientImpact - Raw patient impact level value to translate.
 * @returns The translated label, or the original value when untranslated.
 */
const gettextPatientImpact = (patientImpact: any) =>
  (ADDED_TAG_PATIENT_IMPACT as any)[patientImpact] || patientImpact;

const ADDED_TAG_RMA_STATUS = {
  'All statuses': t`All statuses`,
  Pending: t`Pending`,
  Approved: t`Approved`,
  Compledted: t`Compledted`,
  Canceled: t`Canceled`,
};
/**
 * Resolves the translated label for a RMA status. Looks the raw value up in the ADDED_TAG_RMA_STATUS message map and falls back to the value itself when no translation is registered.
 *
 * @param rmaStatus - Raw RMA status value to translate.
 * @returns The translated label, or the original value when untranslated.
 */
const gettextRmaStatus = (rmaStatus: any) => (ADDED_TAG_RMA_STATUS as any)[rmaStatus] || rmaStatus;

const ADDED_TAG_ALL_DEVICES_SUBTAB = {
  'In Study': t`In Study`,
  'Awaiting upload': t`Awaiting upload`,
  'Not in study': t`Not in study`,
};
/**
 * Resolves the translated label for a all-devices subtab name. Looks the raw value up in the ADDED_TAG_ALL_DEVICES_SUBTAB message map and falls back to the value itself when no translation is registered.
 *
 * @param subtab - Raw all-devices subtab name value to translate.
 * @returns The translated label, or the original value when untranslated.
 */
const gettextAllDevicesSubtab = (subtab: any) =>
  (ADDED_TAG_ALL_DEVICES_SUBTAB as any)[subtab] || subtab;

const ADDED_TAG_SYMPTOMS = {
  Baseline: t`Baseline`,
  'Resume Study': t`Resume Study`,
  'Evaluation Strip': t`Evaluation Strip`,
  'Chest Pain or Pressure': t`Chest Pain or Pressure`,
  'Shortness of Breath': t`Shortness of Breath`,
  Palpitations: t`Palpitations`,
  Dizziness: t`Dizziness`,
  Other: t`Other`,
  'Not specified': t`Not specified`,
};
/**
 * Resolves the translated label for a patient symptom. Looks the raw value up in the ADDED_TAG_SYMPTOMS message map and falls back to the value itself when no translation is registered.
 *
 * @param symtom - Raw patient symptom value to translate.
 * @returns The translated label, or the original value when untranslated.
 */
const gettextSymptoms = (symtom: any) => (ADDED_TAG_SYMPTOMS as any)[symtom] || symtom;

export {
  gettextAllDevicesSubtab,
  gettextDeviceModel,
  gettextDeviceNotificationHistory,
  gettextEditPatientDetailsTitle,
  gettextEventType,
  gettextFacilityName,
  gettextGender,
  gettextPatientImpact,
  gettextReceivedItem,
  gettextReportType,
  gettextRmaStatus,
  gettextSeeAllPatients,
  gettextStudyStatus,
  gettextStudyType,
  gettextSupportRequestAction,
  gettextSymptoms,
  getttextPriority,
};
